import classnames from 'classnames'
import { RefObject, useMemo, useRef } from 'react'

import { IWebsiteProduct } from '@src/components/product-card/product-card.types'
import LAYOUT_TYPES from '@src/quince-site-common/src/constants/layoutTypes'
import { TrackingMetaData } from '@src/quince-site-common/src/domains/CustomSection'
import { useBatchedAnalyticsCaptureForNewProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsCaptureForProductCard'
import useDeviceInfo from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIsVisible from '@src/quince-site-common/src/hooks/useIsVisible'
import getMaxWidthInPx from '@src/quince-site-common/src/utils/getMaxWidthInPx'

import { RichText, RichTextProps } from '../../molecules'
import Button, { ButtonProps } from '../button'
import Image, { ImageProps } from '../image/Image'

import styles from './textWithImageItem.module.scss'
import useButtonClickWithTracking from './useButtonClickWithProductTracking'

export enum IMAGE_POSITION {
  UP = 'up',
  DOWN = 'down',
}

export interface TextWithImageItemProps {
  id: string
  descriptionRichTextProps?: RichTextProps
  authorNameRichTextProps?: RichTextProps
  imageProps?: ImageProps
  buttonProps?: ButtonProps
  imagePosition?: IMAGE_POSITION
  className?: string
  layoutType?: LAYOUT_TYPES
  bulkItemViewTracker?: string
  product?: IWebsiteProduct
  trackingMetaData?: TrackingMetaData
  tracker?: string
  productPosition?: number
}

export const TextWithImageItem = ({
  imageProps,
  imagePosition = IMAGE_POSITION.UP,
  descriptionRichTextProps,
  authorNameRichTextProps,
  buttonProps,
  className = '',
  layoutType = LAYOUT_TYPES.DEFAULT,
  bulkItemViewTracker,
  product,
  trackingMetaData,
  tracker,
  productPosition,
}: TextWithImageItemProps) => {
  const wrapClassNames = classnames(
    styles.slideWrapper,
    styles.slideWrapper__clickable,
    styles[`imagePosition__${imagePosition || IMAGE_POSITION.DOWN}`],
    className,
  )

  const deviceInfo = useDeviceInfo()
  const maxWidth = getMaxWidthInPx({
    maxWidth: styles.imageWidth || '',
    deviceType: deviceInfo.deviceType,
  })

  const wrapperRef: RefObject<HTMLDivElement> = useRef(null)
  const { isVisible } = useIsVisible(wrapperRef, 0.5)
  const selectedVariant = useMemo(() => {
    return product?.cardVariants?.[0]
  }, [product])
  const applicableTags = useMemo(() => selectedVariant?.tags ?? [], [selectedVariant])
  useBatchedAnalyticsCaptureForNewProductCard({
    applicableTags,
    isVisible,
    productCard: product ? { product } : undefined,
    productViewTracker: bulkItemViewTracker,
    wrapperRef,
    selectedVariant,
  })

  const onButtonClick = useButtonClickWithTracking(
    product,
    buttonProps?.href,
    trackingMetaData,
    tracker,
    productPosition,
  )

  return (
    <div className={wrapClassNames} onClick={onButtonClick} ref={wrapperRef}>
      {imageProps && (
        <Image
          alt={imageProps.alt}
          {...imageProps}
          maxWidth={maxWidth}
          className={classnames(styles.image, imageProps.className)}
          // @i18n-ignore
          loading={'lazy'}
        />
      )}
      <div className={styles.contentWrap}>
        {descriptionRichTextProps && (
          <RichText
            {...descriptionRichTextProps}
            className={classnames(styles.description, descriptionRichTextProps.className, {
              [styles.description__extraMargin ?? '']: !authorNameRichTextProps && !buttonProps,
              [styles.description__richTextWithsubText ?? '']: layoutType === LAYOUT_TYPES.RICH_TEXT_WITH_SUBTEXT,
            })}
          />
        )}
        {authorNameRichTextProps && (
          <RichText
            {...authorNameRichTextProps}
            className={classnames(
              {
                [styles.authorName ?? '']: layoutType === LAYOUT_TYPES.REVIEW_SLIDER,
                [styles.subTextContainer ?? '']: layoutType === LAYOUT_TYPES.RICH_TEXT_WITH_SUBTEXT,
              },
              authorNameRichTextProps.className,
            )}
          />
        )}
        {buttonProps && (
          <Button
            {...buttonProps}
            href={product ? undefined : buttonProps.href}
            onClick={onButtonClick}
            className={classnames(styles.actionButton, buttonProps.className)}
          />
        )}
      </div>
    </div>
  )
}

export default TextWithImageItem
